import React, { useEffect, useRef, useState } from "react";
import PromptSession from "../classes/prompt-session";
import MotdComponent from "../components/Motd";
import { useIsMobile } from "../hooks/useIsMobile";
import { useKeyboardInput } from "../hooks/useKeyboardInput";
import { useScrollToBottom } from "../hooks/useScrollToBottom";
import Prompt from "./Prompt";
import ResultDiv from "./ResultDiv";
import TerminalTitle from "./TerminalTitle";

const TerminalContainer = ({ handleTerminalChange }) => {
  const isMobile = useIsMobile();

  const [isMotdVisible, setIsMotdVisible] = useState(true);
  const [terminalClasses, setTerminalClasses] = useState(
    "scale-100 opacity-100"
  );

  const handleTerminalClose = () => {
    setTerminalClasses("scale-100 opacity-0");
    setIsMotdVisible(false);
    handleTerminalChange(false);
  };

  const bottomRef = useRef(null);
  useScrollToBottom(bottomRef.current);

  const [prompts, setPrompts] = useState([new PromptSession()]);

  const handleEnterPress = () => {
    if (promptText.trim().toLowerCase().split(" ")[0] === "clear") {
      setPrompts([new PromptSession()]);
      setIsMotdVisible(false);
      return;
    }

    setPrompts((prev) => {
      prev[prev.length - 1].handleEnterClick(promptText);
      return [...prev, new PromptSession()];
    });
  };

  const promptText = useKeyboardInput(handleEnterPress, () => {
    setPrompts([new PromptSession()]);
    setIsMotdVisible(false);
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [prompts, promptText]);

  return (
    <section
      className={`terminal-container rounded-md min-h-[60vh] max-h-[60vh] ${
        isMobile ? "w-[95vw]" : "md:min-w-[60vw] md:max-w-[60vw]"
      } bg-kali-gray-dark/[.96]
        border border-solid border-kali-border font-fira-code
        text-sm shadow-terminal flex flex-col overflow-hidden
        cursor-default resize ${terminalClasses} transition-all duration-100`}
      style={{ position: "relative", zIndex: 9998 }}
    >
      {!isMobile && <TerminalTitle closeTerminal={handleTerminalClose} />}
      {isMobile && (
        <div className="bg-kali-gray-dark px-3 py-2 flex justify-between items-center border-b border-kali-border">
          <div className="text-kali-gray-light font-medium">Terminal Mode</div>
          <button
            onClick={handleTerminalClose}
            className="text-kali-red-light hover:text-white font-bold px-3 py-1.5 rounded bg-red-800/30 border border-red-700/50 shadow-sm"
          >
            Close
          </button>
        </div>
      )}

      <div
        className="px-1 text-kali-gray text-sm w-full h-full
          overflow-y-auto terminal-scrollbar pb-12 pt-1"
      >
        {isMobile && (
          <div className="p-4 mb-4 border border-kali-red-dark/30 bg-kali-red-dark/10 rounded">
            <ResultDiv
              text={`<span class="text-kali-red-light font-semibold">Terminal Mode is limited on mobile devices</span>
<span class="text-kali-gray-light">
<br/>• Terminal requires a physical keyboard for best experience
<br/>• Some commands may not work properly
<br/>• For full functionality, please use a desktop device
<br/>• Try tapping the terminal window to activate the mobile keyboard</span>`}
            />
            <div className="mt-3 border-t border-kali-red-dark/20 pt-2">
              <span className="text-kali-gray-light text-xs">
                Tip: You can still close this terminal and navigate the
                portfolio using the navigation dock below.
              </span>
            </div>
          </div>
        )}
        {/* {isMotdVisible && <ResultDiv text={motdText()} />} */}
        {isMotdVisible && <MotdComponent />}

        {prompts.map((prompt) => {
          return (
            <div key={`prompt-${Math.random()}`}>
              <Prompt
                text={prompt.enterPressed ? prompt.promptText : promptText}
                showCursor={prompt.showCursor}
              />

              {prompt.result !== undefined && (
                <ResultDiv text={prompt.result} />
              )}
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {isMobile && (
        <div className="border-t border-kali-border p-2 bg-kali-gray-dark/90">
          <div className="text-kali-gray-light text-center text-xs flex items-center justify-center">
            <span>Tap here for keyboard</span>
            <button
              onClick={handleTerminalClose}
              className="text-white font-medium mx-2 px-3 py-1 bg-red-700 rounded-md shadow-sm"
            >
              Close Terminal
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default TerminalContainer;
